import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, CircleDollarSign, Crown, Rocket, Sparkles, Star, Trophy, Zap } from "lucide-react";
import { useNavigate } from "react-router-dom";
import API from "../services/api";

const NORMAL_RATE_META = [
  { key: "single", title: "Single", desc: "Choose any single digit from 0-9", icon: CircleDollarSign, accent: "#2563eb", iconBg: "linear-gradient(135deg,#4f8df7,#2563eb)" },
  { key: "jodi", title: "Jodi", desc: "Select a pair of digits from 00-99", icon: Sparkles, accent: "#9333ea", iconBg: "linear-gradient(135deg,#b45cff,#9333ea)" },
  { key: "red_bracket", title: "Red Bracket", desc: "Bracket-based number play", icon: Rocket, accent: "#ef4444", iconBg: "linear-gradient(135deg,#fb7185,#ef4444)" },
  { key: "sp", title: "Single Patti", desc: "Pick a three-digit number", icon: Star, accent: "#16a34a", iconBg: "linear-gradient(135deg,#22c55e,#16a34a)" },
  { key: "dp", title: "Double Patti", desc: "Choose a three-digit number with two same digits", icon: Zap, accent: "#ea580c", iconBg: "linear-gradient(135deg,#f59e0b,#ea580c)" },
  { key: "tp", title: "Triple Patti", desc: "Select a three-digit number with all same digits", icon: Trophy, accent: "#ef4444", iconBg: "linear-gradient(135deg,#fb7185,#ef4444)" },
  { key: "half_sangam", title: "Half Sangam", desc: "Combine Ank and Patti for bigger wins", icon: CircleDollarSign, accent: "#4f46e5", iconBg: "linear-gradient(135deg,#6366f1,#4f46e5)" },
  { key: "full_sangam", title: "Full Sangam", desc: "Match both open and close Patti", icon: Crown, accent: "#db2777", iconBg: "linear-gradient(135deg,#ec4899,#db2777)" },
];

const STARLINE_RATE_META = [
  { key: "single", title: "Single", desc: "Choose any single digit from 0-9", icon: CircleDollarSign, accent: "#2563eb", iconBg: "linear-gradient(135deg,#4f8df7,#2563eb)" },
  { key: "sp", title: "Single Patti", desc: "Pick a three-digit number", icon: Star, accent: "#16a34a", iconBg: "linear-gradient(135deg,#22c55e,#16a34a)" },
  { key: "dp", title: "Double Patti", desc: "Choose a three-digit number with two same digits", icon: Zap, accent: "#ea580c", iconBg: "linear-gradient(135deg,#f59e0b,#ea580c)" },
  { key: "tp", title: "Triple Patti", desc: "Select a three-digit number with all same digits", icon: Trophy, accent: "#ef4444", iconBg: "linear-gradient(135deg,#fb7185,#ef4444)" },
];

function UserGameRates() {
  const navigate = useNavigate();
  const isMobile = typeof window !== "undefined" ? window.innerWidth <= 520 : true;
  const [normalRateMap, setNormalRateMap] = useState({});
  const [starlineRateMap, setStarlineRateMap] = useState({});

  useEffect(() => {
    let ignore = false;
    const loadRates = async () => {
      try {
        const [normalRes, starlineRes] = await Promise.all([
          API.get("/game-rates?market_type=normal"),
          API.get("/game-rates?market_type=starline"),
        ]);
        if (!ignore) {
          setNormalRateMap(normalRes.data || {});
          setStarlineRateMap(starlineRes.data || {});
        }
      } catch {
        if (!ignore) {
          setNormalRateMap({});
          setStarlineRateMap({});
        }
      }
    };
    loadRates();
    return () => {
      ignore = true;
    };
  }, []);

  const normalRates = useMemo(() => buildRateCards(NORMAL_RATE_META, normalRateMap), [normalRateMap]);
  const starlineRates = useMemo(() => buildRateCards(STARLINE_RATE_META, starlineRateMap), [starlineRateMap]);

  return (
    <div style={page}>
      <header style={{ ...header, ...(isMobile ? headerMobile : null) }}>
        <button type="button" style={{ ...backButton, ...(isMobile ? backButtonMobile : null) }} onClick={() => navigate(-1)}>
          <ArrowLeft size={isMobile ? 19 : 22} />
        </button>
        <h1 style={{ ...title, ...(isMobile ? titleMobile : null) }}>Game Rates</h1>
        <span style={headerSpacer} />
      </header>

      <main style={{ ...content, ...(isMobile ? contentMobile : null) }}>
        <section style={{ ...heroCard, ...(isMobile ? heroCardMobile : null) }}>
          <div style={heroGlow} />
          <div style={heroText}>
            <strong style={heroTitle}>Best Payout Rates</strong>
            {!isMobile && <p style={heroSubtitle}>Simple, clean and mobile-friendly rates for normal market and starline.</p>}
          </div>
        </section>

        <div style={sectionTitleWrap}>
          <span style={{ ...sectionPill, ...(isMobile ? sectionPillMobile : null) }}>Main Market Rates</span>
        </div>

        <section style={{ ...section, ...(isMobile ? sectionMobile : null) }}>
          {normalRates.map((item) => (
            <RateCard key={item.title} item={item} isMobile={isMobile} />
          ))}
        </section>

        <div style={{ ...starlineBanner, ...(isMobile ? starlineBannerMobile : null) }}>
          <Crown size={isMobile ? 14 : 18} />
          <span>King Starline Rates</span>
        </div>

        <section style={{ ...section, ...(isMobile ? sectionMobile : null) }}>
          {starlineRates.map((item) => (
            <RateCard key={`starline-${item.title}`} item={item} isMobile={isMobile} />
          ))}
        </section>
      </main>
    </div>
  );
}

function buildRateCards(meta, rateMap) {
  return meta
    .map((item) => ({
      ...item,
      rateValue: Number(rateMap?.[item.key] || 0),
      rate: Number(rateMap?.[item.key] || 0) > 0 ? `₹1 → ₹${Number(rateMap[item.key]).toFixed(2)}` : "Updating...",
    }))
    .filter((item) => item.rateValue > 0 || String(item.key).length);
}

function formatKaRate(value) {
  const rate = Number(value || 0);
  if (!Number.isFinite(rate) || rate <= 0) return "Updating...";
  const payout = rate * 10;
  const formatted = Number.isInteger(payout) ? String(payout) : payout.toFixed(2).replace(/\.?0+$/, "");
  return `10 ka ${formatted}`;
}

function RateCard({ item, isMobile }) {
  const Icon = item.icon;

  return (
    <article style={{ ...card, ...(isMobile ? cardMobile : null) }}>
      <span style={{ ...iconWrap, ...(isMobile ? iconWrapMobile : null), background: item.iconBg }}>
        <Icon size={isMobile ? 16 : 20} color="#fff" />
      </span>
      <div style={body}>
        <div style={headlineRow}>
          <strong style={{ ...cardTitle, ...(isMobile ? cardTitleMobile : null) }}>{item.title}</strong>
          <strong style={{ ...rateTextInline, ...(isMobile ? rateTextInlineMobile : null), color: item.accent }}>{formatKaRate(item.rateValue)}</strong>
        </div>
        {!isMobile && <p style={cardDesc}>{item.desc}</p>}
      </div>
    </article>
  );
}

const page = {
  minHeight: "100vh",
  background: "radial-gradient(circle at top, #eef4ff 0%, #f8fafc 34%, #f8fafc 100%)",
  color: "#0f172a"
};
const header = {
  position: "sticky",
  top: 0,
  zIndex: 10,
  height: 62,
  background: "rgba(255,255,255,0.92)",
  backdropFilter: "blur(10px)",
  borderBottom: "1px solid #e5e7eb",
  display: "grid",
  gridTemplateColumns: "48px 1fr 48px",
  alignItems: "center",
  padding: "0 14px"
};
const headerMobile = {
  height: 50,
  gridTemplateColumns: "40px 1fr 40px",
  padding: "0 8px",
};
const backButton = {
  width: 36,
  height: 36,
  border: 0,
  background: "transparent",
  display: "grid",
  placeItems: "center",
  color: "#1f2937",
  cursor: "pointer"
};
const backButtonMobile = { width: 32, height: 32 };
const title = { margin: 0, justifySelf: "center", fontSize: 20, fontWeight: 900, color: "#1d4ed8" };
const titleMobile = { fontSize: 17 };
const headerSpacer = { width: 36 };
const content = { maxWidth: 560, margin: "0 auto", padding: "16px 12px 32px", display: "grid", gap: 16 };
const contentMobile = { padding: "8px 8px 16px", gap: 8 };
const heroCard = {
  position: "relative",
  overflow: "hidden",
  minHeight: 92,
  borderRadius: 22,
  padding: "18px 18px",
  background: "linear-gradient(135deg,#ffffff 0%,#eef5ff 100%)",
  border: "1px solid #dbeafe",
  boxShadow: "0 14px 30px rgba(37,99,235,0.10)"
};
const heroCardMobile = {
  minHeight: 44,
  padding: "10px 12px",
  borderRadius: 16,
  boxShadow: "0 8px 16px rgba(37,99,235,0.08)",
};
const heroGlow = {
  position: "absolute",
  right: -24,
  top: -28,
  width: 120,
  height: 120,
  borderRadius: "999px",
  background: "radial-gradient(circle, rgba(96,165,250,.32) 0%, rgba(96,165,250,0) 72%)"
};
const heroText = { position: "relative", display: "grid", gap: 6 };
const heroTitle = { fontSize: 20, fontWeight: 900, color: "#0f172a" };
const heroSubtitle = { margin: 0, fontSize: 13, lineHeight: 1.5, color: "#64748b", maxWidth: 360 };
const sectionTitleWrap = { display: "flex", justifyContent: "center" };
const sectionPill = {
  minHeight: 48,
  padding: "0 18px",
  borderRadius: 16,
  background: "#fff",
  border: "1px solid #dbeafe",
  color: "#2563eb",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 16,
  fontWeight: 900,
  boxShadow: "0 8px 20px rgba(37,99,235,0.05)"
};
const sectionPillMobile = {
  minHeight: 34,
  padding: "0 12px",
  borderRadius: 12,
  fontSize: 13.5,
  boxShadow: "0 5px 12px rgba(37,99,235,0.04)",
};
const section = { display: "grid", gap: 14 };
const sectionMobile = { gap: 7 };
const card = {
  display: "grid",
  gridTemplateColumns: "56px minmax(0,1fr)",
  alignItems: "center",
  gap: 14,
  background: "linear-gradient(180deg,#ffffff 0%,#fbfdff 100%)",
  border: "1px solid #dbeafe",
  borderRadius: 20,
  boxShadow: "0 8px 18px rgba(15,23,42,0.05)"
};
const cardMobile = {
  gridTemplateColumns: "38px minmax(0,1fr)",
  gap: 8,
  minHeight: 58,
  padding: "8px 10px",
  borderRadius: 14,
  boxShadow: "0 5px 12px rgba(15,23,42,0.045)",
};
const iconWrap = {
  borderRadius: 16,
  display: "grid",
  placeItems: "center",
  boxShadow: "0 10px 18px rgba(37,99,235,0.18)"
};
const iconWrapMobile = {
  width: 34,
  height: 34,
  borderRadius: 11,
  boxShadow: "0 6px 12px rgba(37,99,235,0.14)",
};
const body = { display: "grid", gap: 6, minWidth: 0 };
const headlineRow = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 };
const cardTitle = { fontSize: 15, fontWeight: 800, color: "#1f2937" };
const cardTitleMobile = { fontSize: 13.5 };
const cardDesc = { margin: 0, color: "#6b7280", fontSize: 13, lineHeight: 1.45 };
const rateTextInline = { fontSize: 15, fontWeight: 900, whiteSpace: "nowrap" };
const rateTextInlineMobile = { fontSize: 13.5 };
const starlineBanner = {
  minHeight: 54,
  borderRadius: 18,
  background: "linear-gradient(135deg,#ffffff 0%,#eef5ff 100%)",
  border: "1px solid #bfdbfe",
  color: "#2563eb",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 10,
  fontSize: 17,
  fontWeight: 900,
  boxShadow: "0 10px 22px rgba(37,99,235,0.07)"
};
const starlineBannerMobile = {
  minHeight: 36,
  borderRadius: 13,
  gap: 7,
  fontSize: 13.5,
  boxShadow: "0 6px 14px rgba(37,99,235,0.055)",
};

export default UserGameRates;
